var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, createContext, forwardRef, useContext, } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { hideVisually, focusOutline } from '../../styles/style-mixins';
import { uniqueId } from '../../util/id';
import { useClickEvent } from '../../hooks/useClickEvent';
import { AccessibilityError } from '../../util/errors';
import { deprecate } from '../../util/logger';
import { FieldDescription } from "../FieldAtoms";
const labelBaseStyles = ({ theme }) => css `
  color: var(--cui-fg-normal);
  display: inline-block;
  padding-left: 26px;
  position: relative;
  cursor: pointer;

  &::before {
    box-sizing: border-box;
    height: 18px;
    width: 18px;
    background-color: var(--cui-bg-normal);
    border: 1px solid var(--cui-border-normal);
    border-radius: 100%;
    content: '';
    display: block;
    position: absolute;
    top: calc(${theme.typography.body.one.lineHeight} / 2);
    left: 0;
    transform: translateY(-50%);
    transition: border ${theme.transitions.default};
  }

  &::after {
    box-sizing: border-box;
    height: 10px;
    width: 10px;
    background-color: var(--cui-fg-accent);
    border-radius: 100%;
    content: '';
    display: block;
    position: absolute;
    top: calc(${theme.typography.body.one.lineHeight} / 2);
    left: ${theme.spacings.bit};
    transform: translateY(-50%) scale(0, 0);
    opacity: 0;
    transition: transform ${theme.transitions.default},
      opacity ${theme.transitions.default};
  }
`;
const labelInvalidStyles = ({ invalid }) => invalid &&
    css `
    &:not(:focus)::before {
      border-color: var(--cui-border-danger);
      background-color: var(--cui-bg-danger);
    }

    &:not(:focus)::after {
      background-color: var(--cui-fg-danger);
    }
  `;
const RadioButtonLabel = styled('label')(labelBaseStyles, labelInvalidStyles);
const inputBaseStyles = css `
  ${hideVisually()};

  &:hover + label::before {
    border-color: var(--cui-border-normal-hovered);
  }

  &:focus + label::before {
    ${focusOutline()};
    border-color: var(--cui-border-accent);
  }

  &:focus:not(:focus-visible) + label::before {
    box-shadow: none;
    border-color: var(--cui-border-normal);
  }

  &:checked:focus:not(:focus-visible) + label::before {
    border-color: var(--cui-border-accent);
  }

  &:checked + label {
    &::before {
      border-color: var(--cui-border-accent);
    }

    &::after {
      transform: translateY(-50%) scale(1, 1);
      opacity: 1;
    }
  }

  &:disabled + label,
  &:disabled + label span,
  &[disabled] + label,
  &[disabled] + label span {
    pointer-events: none;
    color: var(--cui-fg-normal-disabled);

    &::before {
      border-color: var(--cui-border-normal-disabled);
      background-color: var(--cui-bg-normal-disabled);
    }

    &::after {
      background-color: var(--cui-fg-on-strong-disabled);
    }
  }

  &:disabled:checked + label,
  &[disabled]:checked + label {
    &::before {
      border-color: var(--cui-border-accent-disabled);
    }

    &::after {
      background-color: var(--cui-fg-accent-disabled);
    }
  }
`;
const inputInvalidStyles = ({ invalid }) => invalid &&
    css `
    &:hover + label::before,
    &:focus + label::before {
      border-color: var(--cui-border-danger-hovered);
    }

    &:checked + label::before {
      border-color: var(--cui-border-danger);
    }
  `;
const RadioButtonInput = styled('input')(inputBaseStyles, inputInvalidStyles);
export const RadioButtonGroupContext = createContext(false);
/**
 * @deprecated Use the {@link RadioButtonGroup} component instead.
 */
export const RadioButton = forwardRef((_a, ref) => {
    var { onChange, label, description, id: customId, name, value, checked, invalid, disabled, tracking, className, style } = _a, props = __rest(_a, ["onChange", "label", "description", "id", "name", "value", "checked", "invalid", "disabled", "tracking", "className", "style"]);
    const isInsideGroup = useContext(RadioButtonGroupContext);
    if (process.env.NODE_ENV !== 'production' && !isInsideGroup) {
        deprecate('RadioButton', 'The RadioButton component has been deprecated. Use the RadioButtonGroup component instead.');
    }
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('RadioButton', 'The `label` prop is missing.');
    }
    const id = customId || uniqueId('radio-button_');
    const handleChange = useClickEvent(onChange, tracking, 'radio-button');
    return (_jsxs(Fragment, { children: [_jsx(RadioButtonInput, Object.assign({}, props, { type: "radio", name: name, id: id, value: value, invalid: invalid, "aria-invalid": invalid && 'true', disabled: disabled, checked: checked, onChange: handleChange, ref: ref })), _jsxs(RadioButtonLabel, Object.assign({ htmlFor: id, invalid: invalid, className: className, style: style, "aria-describedby": description ? `${id}-description` : undefined }, { children: [label, description && (_jsx(FieldDescription, Object.assign({ "aria-hidden": "true" }, { children: description })))] })), description && (_jsx("p", Object.assign({ id: `${id}-description`, css: hideVisually }, { children: description })))] }));
});
RadioButton.displayName = 'RadioButton';
